/**
 * branch-collection.js — Dislicores
 *
 * Consulta al App Proxy qué productos están disponibles en la sucursal y usa
 * esa respuesta para priorizar las grillas marcadas con [data-branch-grid].
 *
 * Las cards sin stock NO se eliminan: se marcan como “¡Pronto de vuelta!” y
 * se mueven al final de la página actual, conservando la paginación de Shopify.
 *
 * Contrato del endpoint:
 *   GET https://sioconn.dislicores.com/poligonos/collection?handle=&location=&limit=&page_cursor=
 *   -> { products: [{ id, title, handle, url, image, price, compareAtPrice }],
 *        nextCursor: string|null }
 *
 * Si NO hay sucursal seleccionada no hace nada: la grilla Liquid nativa queda
 * intacta (importante para SEO). Si el fetch falla, restaura/mantiene la grilla
 * nativa y loguea un warning. Nunca deja la grilla vacía por un error.
 *
 * Markup soportado en el contenedor de la grilla:
 *   data-branch-grid                  activa la carga por sucursal
 *   data-collection-handle="vinos"    handle a pedir al endpoint
 *   data-limit="24"                   productos por página
 *   data-branch-load-more             muestra el botón "Cargar más" (nextCursor)
 *   data-branch-skip-when-filtered    no cargar si hay filtros/orden activos
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'sucursal_store';
  var ENDPOINT = 'https://sioconn.dislicores.com/poligonos/collection';
  var GRID_SELECTOR = '[data-branch-grid]';
  // Tope de disponibilidad a pedir. Debe superar el `limit` de cualquier
  // seccion para que la lista cubra todo lo que Liquid pudo rendir.
  // 300 es el maximo por pagina del backend (pedir mas devuelve 300 igual y lo
  // informa en `limit`); colecciones mas grandes se completan por cursor.
  var FILTER_FETCH_LIMIT = 300;
  var DEFAULT_LIMIT = 24;

  // Este producto no depende de inventario por sucursal.
  var GIFT_CARD_TITLE = 'tarjetas de regalo';
  var GIFT_CARD_HANDLE = 'tarjetas-de-regalo';

  // El backend espera el ID numérico de la location (123), no el GID completo
  // (gid://shopify/Location/123). Poner en false para mandar el GID tal cual.
  var USE_NUMERIC_LOCATION_ID = true;

  var STRINGS = {
    loadMore: 'Cargar más',
    loading: 'Cargando...',
    emptyTitle: 'No hay productos disponibles en tu sucursal seleccionada',
    emptyText: 'Selecciona otra sucursal para consultar disponibilidad.',
    // Mismos textos que locales/es.json (products.product.*), porque este
    // markup replica el de card-product.liquid y debe leerse igual.
    addToCart: 'Agregar',
    chooseOptions: 'Elige opciones',
    soldOut: 'Agotado',
    decrease: 'Disminuir cantidad',
    increase: 'Aumentar cantidad'
  };

  // Copiados de snippets/icon-minus.liquid e icon-plus.liquid: el theme los
  // inyecta via Liquid, que aqui no esta disponible.
  var ICONS = {
    minus:
      '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"' +
      ' class="icon icon-minus icon--medium icon--thick" fill="none" viewBox="0 0 20 20">' +
      '<path d="M3.125 10H16.875" stroke="currentColor" stroke-width="1.5"' +
      ' stroke-linecap="round" stroke-linejoin="round"/></svg>',
    plus:
      '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"' +
      ' class="icon icon-plus icon--medium icon--thick" fill="none" viewBox="0 0 20 20">' +
      '<path d="M3.125 10H16.875" stroke="currentColor" stroke-width="1.5"' +
      ' stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M10 3.125V16.875" stroke="currentColor" stroke-width="1.5"' +
      ' stroke-linecap="round" stroke-linejoin="round"/></svg>'
  };

  // ── SUCURSAL ──────────────────────────────────────────────────
  function getBranchState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function normalizeLocationId(id) {
    if (!id) return null;
    var value = String(id);
    return USE_NUMERIC_LOCATION_ID ? value.split('/').pop() : value;
  }

  function getLocationId() {
    var state = getBranchState();
    return state ? normalizeLocationId(state.id) : null;
  }

  // ── UTILIDADES ────────────────────────────────────────────────
  function escapeHtml(value) {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }


  function normalizeText(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  function normalizeHandle(value) {
    return normalizeText(value).replace(/\s+/g, '-');
  }

  function isGiftCardProduct(product) {
    if (!product) return false;

    var title = normalizeText(product.title);
    var handle = normalizeHandle(product.handle);

    if (!handle && product.url) {
      var match = String(product.url).match(/\/products\/([^/?#]+)/i);
      if (match) handle = normalizeHandle(match[1]);
    }

    return title === GIFT_CARD_TITLE || handle === GIFT_CARD_HANDLE;
  }

  function isGiftCardColumn(column) {
    if (!column || !column.querySelector) return false;

    var titleEl = column.querySelector('.product-card__title');
    if (titleEl && normalizeText(titleEl.textContent) === GIFT_CARD_TITLE) {
      return true;
    }

    var productLink = column.querySelector('a[href*="/products/"]');
    if (productLink) {
      var href = productLink.getAttribute('href') || '';
      var match = href.match(/\/products\/([^/?#]+)/i);

      if (match && normalizeHandle(match[1]) === GIFT_CARD_HANDLE) {
        return true;
      }
    }

    return false;
  }

  // El endpoint manda el precio en unidades de moneda con decimales
  // ("123124.76"), no en centavos como el objeto product de Shopify. Los
  // formateadores del theme si esperan centavos, asi que se multiplica por 100
  // antes de pasarselos.
  function formatMoney(price) {
    if (price == null || price === '') return '';

    var amount = Math.round(Number(price) * 100);
    if (isNaN(amount)) return '';

    var theme = window.FoxTheme || {};
    var format = (theme.settings && theme.settings.moneyFormat) || '${{amount_no_decimals}}';

    if (theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(amount, format);
    }

    // Fallback COP por si theme.js todavía no cargó
    return '$' + (amount / 100).toLocaleString('es-CO', { maximumFractionDigits: 0 });
  }

  function hasActiveFilters() {
    var params = new URLSearchParams(window.location.search);

    return Array.from(params.keys()).some(function (key) {
      return key.indexOf('filter.') === 0 || key === 'sort_by' || key === 'q';
    });
  }

  // ── ESTILOS ───────────────────────────────────────────────────
  function injectStyles() {
    if (document.getElementById('branch-collection-styles')) return;

    var style = document.createElement('style');
    style.id = 'branch-collection-styles';
    style.textContent = [
      '[data-branch-grid].loading { opacity: .45; pointer-events: none; transition: opacity .15s ease; }',
      '.branch-grid__load-more { display: flex; justify-content: center; width: 100%; margin-top: 3.2rem; }',
      '.branch-grid__empty { max-width: 1180px; margin: 28px auto; padding: 22px 24px; border: 1px solid #d93025;',
      '  background: #fff5f5; color: #8a1f17; border-radius: 12px; text-align: center; font-size: 16px;',
      '  line-height: 1.45; font-weight: 500; }',
      '.branch-grid__empty strong { display: block; font-size: 20px; margin-bottom: 6px; }',
      '.branch-grid__empty span { display: block; }',
      '.branch-card-skeleton { border-radius: var(--blocks-radius, 8px); background: rgba(var(--color-foreground, 0 0 0) / .07);',
      '  animation: branch-card-skeleton-pulse 1.1s ease-in-out infinite; }',
      '@keyframes branch-card-skeleton-pulse { 0%, 100% { opacity: .45; } 50% { opacity: .8; } }'
    ].join('\n');

    document.head.appendChild(style);
  }

  // ── MOLDE DE LA CARD ──────────────────────────────────────────
  // Tomamos las clases reales de la primera card renderizada por Liquid para
  // que las cards del endpoint hereden el estilo exacto del theme (pcard_style,
  // color scheme, alineación, aspect ratio, swiper-slide, etc.).
  function readCardShell(grid) {
    if (grid.__branchShell) return grid.__branchShell;

    var card = grid.querySelector('.product-card');
    var column = card ? card.closest('.f-column') : null;
    var imageWrapper = card ? card.querySelector('.product-card__image-wrapper') : null;
    var image = card ? card.querySelector('.product-card__image--main') : null;
    var info = card ? card.querySelector('.product-card__info') : null;
    var title = card ? card.querySelector('.product-card__title') : null;
    var titleLink = title ? title.querySelector('a') : null;
    var price = card ? card.querySelector('.f-price') : null;

    // Estilo del boton (btn--primary / btn--secondary / ...) tomado de la card
    // nativa; si la grilla venia vacia se usa el valor por defecto del theme.
    var nativeButton = card
      ? card.querySelector('.product-card__action-button')
      : null;
    var buttonStyle = 'btn--secondary';
    if (nativeButton) {
      Array.prototype.forEach.call(nativeButton.classList, function (cls) {
        if (cls.indexOf('btn--') === 0) buttonStyle = cls;
      });
    }

    var alignment = '';
    if (price) {
      Array.prototype.forEach.call(price.classList, function (cls) {
        if (cls.indexOf('f-price--') === 0 && cls !== 'f-price--on-sale' && cls !== 'f-price--sold-out') {
          alignment = cls;
        }
      });
    }

    var shell = {
      columnClass: column ? column.className : 'f-column',
      cardClass: card ? card.className : 'product-card dislicores-card',
      imageWrapperClass: imageWrapper
        ? imageWrapper.className
        : 'product-card__image-wrapper product-card__image-wrapper--main-only bg-none',
      imageClass: image ? image.className : 'media-wrapper product-card__image product-card__image--main',
      aspectRatio: (image && image.style.getPropertyValue('--aspect-ratio')) || '1',
      infoClass: info ? info.className : 'product-card__info',
      titleClass: title ? title.className : 'product-card__title text-pcard-title',
      titleLinkClass: titleLink ? titleLink.className : 'reversed-link block',
      priceAlignmentClass: alignment,
      // El estilo del boton es un setting del theme (settings.pcard_button_style),
      // asi que se toma del boton que ya render Liquid en vez de fijarlo aqui.
      buttonStyleClass: buttonStyle
    };

    grid.__branchShell = shell;

    return shell;
  }

  function buildPrice(product, shell) {
    var price = formatMoney(product.price);
    var compareAt = product.compareAtPrice != null ? Number(product.compareAtPrice) : null;
    var onSale = compareAt != null && !isNaN(compareAt) && compareAt > Number(product.price);

    var classes = ['f-price'];
    if (shell.priceAlignmentClass) classes.push(shell.priceAlignmentClass);
    if (onSale) classes.push('f-price--on-sale');

    return (
      '<div class="' + classes.join(' ') + '">' +
      '<div class="f-price__regular">' +
      '<span class="f-price-item f-price-item--regular">' +
      (onSale ? '' : escapeHtml(price)) +
      '</span>' +
      '</div>' +
      '<div class="f-price__sale">' +
      '<span class="f-price-item f-price-item--sale">' +
      (onSale ? escapeHtml(price) : '') +
      '</span>' +
      (onSale
        ? '<span class="f-price-item f-price-item--regular"><s>' + escapeHtml(formatMoney(compareAt)) + '</s></span>'
        : '') +
      '</div>' +
      '</div>'
    );
  }

  // Acciones de la card: replica el markup de card-product.liquid para que el
  // CSS y los custom elements del theme (product-form, quantity-input) sigan
  // aplicando sobre las cards que genera este script.
  //
  // Tres caminos, los mismos que el snippet Liquid:
  //   sin stock            -> boton deshabilitado "Agotado"
  //   varias variantes o
  //   reglas de cantidad   -> enlace "Elige opciones" a la pagina del producto
  //   caso simple          -> form de agregar al carrito con quantity-input
  //
  // El endpoint filtra por stock, asi que `available:false` no deberia llegar;
  // se contempla igual para no depender de esa garantia.
  function buildActions(product, shell, url) {
    var buttonClass =
      'product-card__atc product-card__action-button btn ' + shell.buttonStyleClass;
    var wrapClass =
      'product-card__actions product-card__main-actions product-card__actions--bottom';

    function label(text) {
      return (
        '<div class="btn__text flex gap-1 items-center">' +
        '<span class="product-card__atc-text product-card__action-text">' +
        escapeHtml(text) +
        '</span>' +
        '</div>'
      );
    }

    if (product.available === false) {
      return (
        '<div class="' + wrapClass + '">' +
        '<button type="button" disabled class="' + escapeHtml(buttonClass) + '">' +
        label(STRINGS.soldOut) +
        '</button>' +
        '</div>'
      );
    }

    var rule = product.quantityRule || {};
    var min = rule.min != null ? Number(rule.min) : 1;
    var max = rule.max != null ? Number(rule.max) : null;
    var step = rule.increment != null ? Number(rule.increment) : 1;
    var hasQtyRules = min > 1 || max != null || step > 1;

    // Con varias variantes no se puede agregar a ciegas: hay que elegir cual.
    // Las reglas de cantidad siguen el mismo camino que el snippet Liquid.
    if (Number(product.variantsCount) > 1 || hasQtyRules) {
      return (
        '<div class="' + wrapClass + '">' +
        '<a href="' + escapeHtml(url) + '" class="' + escapeHtml(buttonClass) + '">' +
        label(STRINGS.chooseOptions) +
        '</a>' +
        '</div>'
      );
    }

    if (!product.variantId) return '';

    var formId = 'branch-add-' + escapeHtml(String(product.variantId));

    return (
      '<form method="post" action="/cart/add" id="' + formId + '"' +
      ' accept-charset="UTF-8" class="' + wrapClass + '"' +
      ' enctype="multipart/form-data" novalidate="novalidate"' +
      ' data-type="add-to-cart-form" is="product-form">' +
      '<input type="hidden" name="form_type" value="product">' +
      '<input type="hidden" name="utf8" value="✓">' +
      '<input type="hidden" name="id" class="product-variant-id" value="' +
      escapeHtml(String(product.variantId)) +
      '">' +
      '<div class="product-card__quantity product-card__quantity--quick-add">' +
      '<div class="product-form__quantity">' +
      '<quantity-input class="quantity product-card__quantity-input"' +
      ' data-product-id="' + escapeHtml(String(product.id).split('/').pop()) + '">' +
      '<button class="quantity__button" name="minus" type="button">' +
      '<span class="visually-hidden">' + escapeHtml(STRINGS.decrease) + '</span>' +
      ICONS.minus +
      '</button>' +
      '<input class="quantity__input" type="number" name="quantity"' +
      ' id="Quantity-' + formId + '" value="' + min + '" step="' + step + '"' +
      ' min="' + min + '"' + (max != null ? ' max="' + max + '"' : '') +
      ' inputmode="numeric" autocomplete="off" form="' + formId + '">' +
      '<button class="quantity__button" name="plus" type="button">' +
      '<span class="visually-hidden">' + escapeHtml(STRINGS.increase) + '</span>' +
      ICONS.plus +
      '</button>' +
      '</quantity-input>' +
      '</div>' +
      '</div>' +
      '<button id="' + formId + '-submit" type="submit" name="add"' +
      ' aria-label="' + escapeHtml(STRINGS.addToCart) + '"' +
      ' class="' + escapeHtml(buttonClass) + '">' +
      label(STRINGS.addToCart) +
      '</button>' +
      '</form>'
    );
  }

  function buildCard(product, shell) {
    var url = product.url || (product.handle ? '/products/' + product.handle : '#');
    var title = escapeHtml(product.title);
    // El resto del theme (compare, quantity-input) lee data-product-id como ID
    // numérico, así que normalizamos por si el endpoint manda un GID.
    var productId = product.id != null ? String(product.id).split('/').pop() : '';

    var media = product.image
      ? '<img src="' +
        escapeHtml(product.image) +
        '" alt="' +
        title +
        '" loading="lazy" decoding="async" class="motion-reduce">'
      : '';

    // Segunda imagen (hover en desktop), igual que card-product.liquid. Solo se
    // pinta si el endpoint la manda: sin ella la card lleva la clase
    // --main-only y el theme no espera ningun hover.
    var secondImage = product.secondImage || product.imageSecond || '';
    var secondMedia = secondImage
      ? '<div class="media-wrapper product-card__image product-card__image--second inset-full hidden md:block"' +
        ' style="--aspect-ratio: ' + escapeHtml(shell.aspectRatio) + '">' +
        '<img src="' + escapeHtml(secondImage) + '" alt="' + title +
        '" loading="lazy" decoding="async" class="motion-reduce">' +
        '</div>'
      : '';

    return (
      '<div class="' +
      escapeHtml(shell.columnClass) +
      '" data-branch-column>' +
      '<div class="' +
      escapeHtml(shell.cardClass) +
      '" data-product-id="' +
      escapeHtml(productId) +
      '" data-branch-card>' +
      '<div class="product-card__wrapper h-full">' +
      // El theme marca --main-only cuando no hay segunda imagen; con hover esa
      // clase debe salir para que el CSS del cambio de imagen aplique.
      '<div class="' +
      escapeHtml(
        secondMedia
          ? shell.imageWrapperClass.replace(
              /product-card__image-wrapper--main-only/g,
              ''
            ).replace(/\s+/g, ' ').trim()
          : shell.imageWrapperClass
      ) +
      '">' +
      '<a href="' +
      escapeHtml(url) +
      '" aria-label="' +
      title +
      '" tabindex="-1">' +
      '<div class="' +
      escapeHtml(shell.imageClass) +
      '" style="--aspect-ratio: ' +
      escapeHtml(shell.aspectRatio) +
      '">' +
      media +
      '</div>' +
      secondMedia +
      '</a>' +
      '</div>' +
      '<div class="' +
      escapeHtml(shell.infoClass) +
      '">' +
      '<h3 class="' +
      escapeHtml(shell.titleClass) +
      '">' +
      '<a class="' +
      escapeHtml(shell.titleLinkClass) +
      '" href="' +
      escapeHtml(url) +
      '">' +
      '<span class="reversed-link__text">' +
      title +
      '</span>' +
      '</a>' +
      '</h3>' +
      buildPrice(product, shell) +
      buildActions(product, shell, url) +
      '</div>' +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  // Columnas que no son productos (banners de card-image en colección, banner
  // del featured-collection). Se guardan con su posición para reinsertarlas
  // después de repintar la grilla.
  function readStaticColumns(grid) {
    if (grid.__branchStatics) return grid.__branchStatics;

    var statics = [];

    Array.prototype.forEach.call(grid.children, function (child, index) {
      if (!child.querySelector('.product-card')) {
        statics.push({ index: index, html: child.outerHTML });
      }
    });

    grid.__branchStatics = statics;

    return statics;
  }

  function restoreStaticColumns(grid) {
    readStaticColumns(grid).forEach(function (item) {
      var holder = document.createElement('div');
      holder.innerHTML = item.html;

      var node = holder.firstElementChild;
      if (!node) return;

      grid.insertBefore(node, grid.children[item.index] || null);
    });
  }


  // Guardamos la card nativa de "Tarjetas de regalo" antes de que el endpoint
  // reemplace la grilla. Esta card nunca se somete al filtro de sucursal.
  function readGiftCardColumns(grid) {
    if (grid.__branchGiftCards) return grid.__branchGiftCards;

    var giftCards = [];

    Array.prototype.forEach.call(grid.children, function (child, index) {
      if (isGiftCardColumn(child)) {
        giftCards.push({ index: index, html: child.outerHTML });
      }
    });

    grid.__branchGiftCards = giftCards;

    return giftCards;
  }

  function restorePreservedColumns(grid) {
    var preserved = readStaticColumns(grid)
      .concat(readGiftCardColumns(grid))
      .sort(function (a, b) {
        return a.index - b.index;
      });

    preserved.forEach(function (item) {
      var holder = document.createElement('div');
      holder.innerHTML = item.html;

      var node = holder.firstElementChild;
      if (!node) return;

      grid.insertBefore(node, grid.children[item.index] || null);
    });
  }

  function buildSkeletons(shell, count) {
    var html = '';

    for (var i = 0; i < count; i++) {
      html +=
        '<div class="' +
        escapeHtml(shell.columnClass) +
        '" data-branch-skeleton>' +
        '<div class="branch-card-skeleton" style="aspect-ratio: ' +
        escapeHtml(shell.aspectRatio) +
        '"></div>' +
        '</div>';
    }

    return html;
  }

  // ── ESTADO POR GRILLA ─────────────────────────────────────────
  function getGridState(grid) {
    if (grid.__branchGrid) return grid.__branchGrid;

    // Leer antes de cualquier reemplazo del HTML.
    readGiftCardColumns(grid);

    var state = {
      el: grid,
      handle: grid.dataset.collectionHandle || '',
      limit: parseInt(grid.dataset.limit, 10) || DEFAULT_LIMIT,
      wantsLoadMore: grid.hasAttribute('data-branch-load-more'),
      skipWhenFiltered: grid.hasAttribute('data-branch-skip-when-filtered'),
      originalHTML: grid.innerHTML,
      rendered: false,
      loadedFor: null,
      cursor: null,
      requestId: 0,
      loadMoreWrap: null,
      loadMoreBtn: null
    };

    grid.__branchGrid = state;

    return state;
  }

  function getNativePagination(grid) {
    var scope = grid.parentElement;
    return scope ? scope.querySelector('.pagination') : null;
  }

  function setLoading(state, isLoading, append) {
    if (!append) {
      state.el.classList.toggle('loading', isLoading);
    }

    state.el.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    if (state.loadMoreBtn) {
      state.loadMoreBtn.disabled = isLoading;
      state.loadMoreBtn.querySelector('.btn__text').textContent = isLoading ? STRINGS.loading : STRINGS.loadMore;
    }
  }

  function removeSkeletons(state) {
    Array.prototype.forEach.call(state.el.querySelectorAll('[data-branch-skeleton]'), function (node) {
      node.remove();
    });
  }

  function getEmptyMessage(state, create) {
    if (state.emptyEl) return state.emptyEl;
    if (!create) return null;

    var message = document.createElement('div');
    message.className = 'branch-grid__empty';
    message.innerHTML =
      '<strong>' + STRINGS.emptyTitle + '</strong>' + '<span>' + STRINGS.emptyText + '</span>';

    state.el.insertAdjacentElement('afterend', message);
    state.emptyEl = message;

    return message;
  }

  function hideEmptyMessage(state) {
    if (state.emptyEl) {
      state.emptyEl.remove();
      state.emptyEl = null;
    }
  }

  function updateLoadMore(state, nextCursor) {
    state.cursor = nextCursor || null;

    if (!state.wantsLoadMore || !nextCursor) {
      if (state.loadMoreWrap) {
        state.loadMoreWrap.remove();
        state.loadMoreWrap = null;
        state.loadMoreBtn = null;
      }
      return;
    }

    if (state.loadMoreWrap) return;

    var wrap = document.createElement('div');
    wrap.className = 'branch-grid__load-more';

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn--primary';
    button.innerHTML = '<span class="btn__text">' + STRINGS.loadMore + '</span>';

    button.addEventListener('click', function () {
      if (!state.cursor) return;

      loadCollectionByBranch(state.handle, getLocationId(), {
        limit: state.limit,
        cursor: state.cursor,
        container: state.el
      });
    });

    wrap.appendChild(button);

    var anchor = state.emptyEl || state.el;
    anchor.insertAdjacentElement('afterend', wrap);

    state.loadMoreWrap = wrap;
    state.loadMoreBtn = button;
  }

  // El slider de featured-collection (Swiper) necesita recalcular sus slides
  // después de que cambiamos el contenido de la grilla.
  function refreshSlider(grid) {
    var slider = grid.closest('featured-collection');

    if (slider && slider.sliderInstance && slider.sliderInstance.slider) {
      slider.sliderInstance.slider.update();
    }
  }

  // ── PRIORIDAD DE PRODUCTOS POR SUCURSAL ───────────────────────
  //
  // Ya NO eliminamos cards sin inventario. La pagina conserva exactamente los
  // productos que Shopify/Liquid rindio y su paginacion nativa. Solamente:
  //   1) marcamos si el producto esta disponible en la sucursal,
  //   2) convertimos su CTA a "¡Pronto de vuelta!" cuando no hay stock,
  //   3) movemos esos productos al final de la pagina actual.
  //
  // Las columnas promocionales (card-image) conservan su posicion: reordenamos
  // unicamente los slots que ya eran de producto.
  function setUnavailableAction(card, unavailable) {
    if (!card) return;

    card.classList.toggle('is-branch-unavailable', unavailable);
    card.setAttribute('data-branch-available', unavailable ? 'false' : 'true');

    var quantities = card.querySelectorAll('.product-card__quantity');
    Array.prototype.forEach.call(quantities, function (quantity) {
      if (typeof quantity.__branchOriginalDisplay === 'undefined') {
        quantity.__branchOriginalDisplay = quantity.style.display || '';
      }

      quantity.style.display = unavailable ? 'none' : quantity.__branchOriginalDisplay;
    });

    var actions = card.querySelectorAll('.product-card__atc, .product-card__list-atc');
    Array.prototype.forEach.call(actions, function (action) {
      if (!action.__branchOriginalState) {
        action.__branchOriginalState = {
          html: action.innerHTML,
          href: action.getAttribute('href'),
          tabindex: action.getAttribute('tabindex'),
          ariaDisabled: action.getAttribute('aria-disabled'),
          pointerEvents: action.style.pointerEvents || '',
          disabled: action.disabled === true
        };
      }

      var original = action.__branchOriginalState;

      if (!unavailable) {
        action.innerHTML = original.html;

        if (original.href === null) action.removeAttribute('href');
        else action.setAttribute('href', original.href);

        if (original.tabindex === null) action.removeAttribute('tabindex');
        else action.setAttribute('tabindex', original.tabindex);

        if (original.ariaDisabled === null) action.removeAttribute('aria-disabled');
        else action.setAttribute('aria-disabled', original.ariaDisabled);

        action.style.pointerEvents = original.pointerEvents;

        if ('disabled' in action) {
          action.disabled = original.disabled;
        }

        action.classList.remove('is-branch-unavailable-action');
        return;
      }

      var text = action.querySelector(
        '.product-card__atc-text, .product-card__action-text, .btn__text'
      );

      if (text) {
        text.textContent = '¡Pronto de vuelta!';
      } else {
        action.textContent = '¡Pronto de vuelta!';
      }

      var icon = action.querySelector('.product-card__atc-icon, .product-card__action-icon');
      if (icon) icon.style.display = 'none';

      action.setAttribute('aria-disabled', 'true');
      action.setAttribute('tabindex', '-1');
      action.classList.add('is-branch-unavailable-action');

      if (action.tagName === 'A') {
        action.removeAttribute('href');
        action.style.pointerEvents = 'none';
      } else if ('disabled' in action) {
        action.disabled = true;
      }
    });
  }

  // ── CONTADOR DE PRODUCTOS ─────────────────────────────────────
  // El contador que pinta Liquid cuenta la coleccion entera, sin mirar
  // sucursal ("439 productos"). El backend ahora devuelve `total` con los
  // disponibles en la sucursal elegida, asi que reescribimos ese texto.
  //
  // Las plantillas vienen del Liquid en data-branch-count-one/other con __N__
  // como marcador, para respetar el idioma y la pluralizacion del theme.
  function updateProductCount(total) {
    if (typeof total !== 'number' || !isFinite(total) || total < 0) return;

    var node = document.getElementById('ProductCount');
    if (!node) return;

    var plantilla =
      total === 1
        ? node.getAttribute('data-branch-count-one')
        : node.getAttribute('data-branch-count-other');

    if (!plantilla) return;

    // Guardamos el texto de Liquid una sola vez para poder restaurarlo si el
    // usuario se queda sin sucursal.
    if (typeof node.__branchOriginalText === 'undefined') {
      node.__branchOriginalText = node.textContent;
    }

    node.textContent = plantilla.replace('__N__', String(total));
    node.setAttribute('data-branch-count-applied', 'true');
  }

  function restoreProductCount() {
    var node = document.getElementById('ProductCount');
    if (!node || node.getAttribute('data-branch-count-applied') !== 'true') return;

    if (typeof node.__branchOriginalText === 'string') {
      node.textContent = node.__branchOriginalText;
    }

    node.removeAttribute('data-branch-count-applied');
  }

  // Los productos sin stock en la sucursal se ocultan por completo. Se usa
  // `hidden` en vez de removerlos para poder devolverlos cuando el usuario
  // cambia de sucursal, sin volver a pedir la pagina.
  function setColumnHidden(columna, hidden) {
    if (!columna) return;

    columna.hidden = hidden;
    columna.classList.toggle('is-branch-hidden', hidden);
  }

  function reorderProductColumns(grid) {
    if (!grid) return;

    // Solo se reordena lo visible: las columnas ocultas no deben abrir huecos
    // ni arrastrar su posicion cuando vuelvan a mostrarse.
    var productColumns = Array.prototype.filter.call(grid.children, function (child) {
      return !!child.querySelector('[data-product-id]') && !child.hidden;
    });

    if (productColumns.length < 2) return;

    var ordered = productColumns.slice().sort(function (a, b) {
      var aCard = a.querySelector('[data-product-id]');
      var bCard = b.querySelector('[data-product-id]');

      var aUnavailable =
        aCard && aCard.getAttribute('data-branch-available') === 'false' ? 1 : 0;
      var bUnavailable =
        bCard && bCard.getAttribute('data-branch-available') === 'false' ? 1 : 0;

      return aUnavailable - bUnavailable;
    });

    var changed = ordered.some(function (node, index) {
      return node !== productColumns[index];
    });

    if (!changed) return;

    // Los comments actuan como slots. De esta forma los banners/promocionales
    // no cambian de lugar aunque reordenemos las cards de producto.
    var slots = productColumns.map(function (column) {
      var slot = document.createComment('branch-product-slot');
      column.parentNode.insertBefore(slot, column);
      column.remove();
      return slot;
    });

    ordered.forEach(function (column, index) {
      slots[index].parentNode.insertBefore(column, slots[index]);
    });

    slots.forEach(function (slot) {
      slot.remove();
    });
  }

  function renderProducts(state, products, append, total) {
    // Este flujo trabaja sobre la pagina actual que Liquid ya renderizo.
    // Nunca altera el tipo de paginacion configurado en Shopify.
    if (append) return;

    var disponibles = {};
    products.forEach(function (product) {
      var id = product && product.id != null ? String(product.id).split('/').pop() : '';
      if (id) disponibles[id] = true;
    });

    removeSkeletons(state);
    hideEmptyMessage(state);

    var columnas = state.el.querySelectorAll('.f-column');

    Array.prototype.forEach.call(columnas, function (columna) {
      var card = columna.querySelector('[data-product-id]');
      if (!card) return;

      // Tarjetas de regalo siempre se consideran disponibles.
      if (isGiftCardColumn(columna)) {
        setUnavailableAction(card, false);
        setColumnHidden(columna, false);
        return;
      }

      var id = String(card.getAttribute('data-product-id') || '').split('/').pop();
      if (!id) return;

      var unavailable = !disponibles[id];
      setUnavailableAction(card, unavailable);
      setColumnHidden(columna, unavailable);
    });

    reorderProductColumns(state.el);

    updateProductCount(total);

    // Si la pagina entera quedo sin stock en esta sucursal, la grilla se ve
    // vacia: hay que decir por que, no dejar un hueco mudo.
    var visibles = Array.prototype.filter.call(state.el.children, function (child) {
      return !!child.querySelector('[data-product-id]') && !child.hidden;
    });

    if (!visibles.length) {
      getEmptyMessage(state, true);
    }

    state.rendered = true;

    // MUY IMPORTANTE:
    // - no ocultar la paginacion nativa
    // - no sustituir el contador de Shopify
    // Los productos sin stock siguen formando parte de la pagina, solo van al final.
    var pagination = getNativePagination(state.el);
    if (pagination) pagination.hidden = false;

    refreshSlider(state.el);
  }

  // Devuelve la grilla al render nativo de Liquid (fallback ante errores o
  // cuando el usuario ya no tiene sucursal seleccionada).
  function restoreNative(state) {
    if (!state.rendered) return;

    state.el.innerHTML = state.originalHTML;
    state.rendered = false;
    state.loadedFor = null;
    state.cursor = null;

    hideEmptyMessage(state);
    updateLoadMore(state, null);
    restoreProductCount();

    var pagination = getNativePagination(state.el);
    if (pagination) pagination.hidden = false;

    refreshSlider(state.el);
  }

  // ── CARGA ─────────────────────────────────────────────────────

  // El backend pagina de 48 en 48 e ignora el `limit` que le mandamos (ver
  // BACKEND-COLLECTION-TOTAL.md). Una sola llamada solo cubre los primeros 48
  // productos, asi que el resto de la coleccion se marcaria como sin stock por
  // error. Mientras el backend no respete `limit` ni devuelva `total`, aqui
  // seguimos `nextCursor` hasta agotar la coleccion.
  //
  // Topes de seguridad: si el backend empieza a devolver cursores que no
  // avanzan, cortamos en vez de colgar la pagina.
  var MAX_CURSOR_PAGES = 20;

  function fetchAllPages(baseUrl) {
    var acumulados = [];
    var vistos = {};
    var paginas = 0;

    function pedir(cursor) {
      var url = cursor ? baseUrl + '&page_cursor=' + encodeURIComponent(cursor) : baseUrl;

      return fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then(function (data) {
          if (!data || !Array.isArray(data.products)) {
            throw new Error('Respuesta inesperada del endpoint');
          }

          acumulados = acumulados.concat(data.products);
          paginas++;

          var siguiente = data.nextCursor || null;

          // El backend ya manda `total`: si lo alcanzamos no hay nada mas que
          // pedir, aunque venga un cursor de mas.
          if (typeof data.total === 'number' && acumulados.length >= data.total) {
            siguiente = null;
          }

          // Cursor repetido = el backend no esta avanzando. Cortar.
          if (siguiente && vistos[siguiente]) siguiente = null;
          if (siguiente) vistos[siguiente] = true;

          if (!siguiente || paginas >= MAX_CURSOR_PAGES) {
            if (siguiente) {
              console.warn(
                '[branch-collection] Se alcanzo el tope de ' +
                  MAX_CURSOR_PAGES +
                  ' paginas; la lista de disponibilidad puede estar incompleta.'
              );
            }

            return { products: acumulados, nextCursor: null, total: data.total };
          }

          return pedir(siguiente);
        });
    }

    return pedir(null);
  }

  function loadCollectionByBranch(handle, locationId, options) {
    options = options || {};

    var container = options.container || null;
    // En modo filtrado la respuesta se usa como lista de disponibilidad, no
    // como fuente de cards: hay que cubrir TODOS los productos que Liquid pudo
    // rendir, no solo los primeros. Si se pidiera el mismo limit que la
    // seccion, los productos rendidos que caen mas abajo en la coleccion no
    // apareceran en la lista y se borrarian por error.
    var limit = FILTER_FETCH_LIMIT;
    var cursor = options.cursor || null;
    var append = !!cursor;

    if (!handle) {
      console.warn('[branch-collection] Falta data-collection-handle, no se puede cargar la grilla.');
      return Promise.resolve(null);
    }

    // Sin sucursal seleccionada no tocamos nada: queda la grilla Liquid.
    if (!locationId) return Promise.resolve(null);

    var url =
      ENDPOINT +
      '?handle=' +
      encodeURIComponent(handle) +
      '&location=' +
      encodeURIComponent(locationId) +
      '&limit=' +
      encodeURIComponent(limit);

    // Con cursor explicito (boton "Cargar mas") se pide solo esa pagina.
    // Sin cursor, la respuesta se usa como lista de disponibilidad de toda la
    // coleccion, asi que hay que recorrerla completa.
    var pedirProductos = cursor
      ? function () {
          return fetch(url + '&page_cursor=' + encodeURIComponent(cursor), {
            headers: { Accept: 'application/json' }
          }).then(function (response) {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.json();
          });
        }
      : function () {
          return fetchAllPages(url);
        };

    var state = container ? getGridState(container) : null;
    var requestId = 0;

    if (state) {
      requestId = ++state.requestId;
      setLoading(state, true, append);

      if (append) {
        state.el.insertAdjacentHTML('beforeend', buildSkeletons(readCardShell(state.el), 3));
      }
    }

    return pedirProductos()
      .then(function (data) {
        if (!data || !Array.isArray(data.products)) {
          throw new Error('Respuesta inesperada del endpoint');
        }

        // Descartamos respuestas que quedaron obsoletas (cambio de sucursal
        // mientras la petición estaba en vuelo).
        if (state && requestId !== state.requestId) return data;

        if (state) {
          renderProducts(state, data.products, append, data.total);
          // No usamos paginación propia del App Proxy: se conserva la paginación
          // nativa de Shopify y solo priorizamos las cards de la página actual.
          updateLoadMore(state, null);
          state.loadedFor = locationId;
        }

        return data;
      })
      .catch(function (error) {
        console.warn(
          '[branch-collection] No se pudo cargar "' +
            handle +
            '" para la sucursal ' +
            locationId +
            '. Se mantiene la grilla nativa.',
          error
        );

        if (state && requestId === state.requestId) {
          removeSkeletons(state);
          restoreNative(state);
        }

        return null;
      })
      .then(function (result) {
        if (state && requestId === state.requestId) setLoading(state, false, append);
        return result;
      });
  }

  // ── GRILLAS REGISTRADAS ───────────────────────────────────────
  function getGrids() {
    return Array.prototype.slice.call(document.querySelectorAll(GRID_SELECTOR));
  }

  function refreshGrid(grid, locationId, force) {
    var state = getGridState(grid);

    if (!locationId) {
      restoreNative(state);
      return Promise.resolve(null);
    }

    // Aunque haya filtros u orden activos, el endpoint se usa solamente como
    // lista de IDs disponibles. La grilla filtrada que rindio Shopify se conserva
    // tal cual y se reordena dentro de la pagina actual.
    // Ya está pintada para esta sucursal (p. ej. al cambiar de tab).
    if (!force && state.rendered && state.loadedFor === locationId) {
      return Promise.resolve(null);
    }

    state.cursor = null;
    updateLoadMore(state, null);

    // Partimos siempre del HTML original para restaurar botones/orden antes de
    // aplicar la disponibilidad de una sucursal distinta.
    if (state.rendered) {
      state.el.innerHTML = state.originalHTML;
      state.rendered = false;
    }

    return loadCollectionByBranch(state.handle, locationId, {
      limit: state.limit,
      container: grid
    });
  }

  function refreshAll(locationId, force) {
    var id = locationId != null ? normalizeLocationId(locationId) : getLocationId();

    return Promise.all(
      getGrids().map(function (grid) {
        return refreshGrid(grid, id, force);
      })
    );
  }

  // ── EVENTOS ───────────────────────────────────────────────────
  window.addEventListener('branch:changed', function (event) {
    var locationId = event && event.detail ? event.detail.locationId : null;
    refreshAll(locationId, true);
  });

  // facets.js reemplaza el HTML de #ProductGridContainer al filtrar/ordenar:
  // las grillas son nodos nuevos y hay que volver a evaluarlas.
  document.addEventListener('collection:rerendered', function () {
    refreshAll();
  });

  // featured-collection con tabs inyecta los paneles desde <template> al
  // cambiar de pestaña: esas grillas recién entran al DOM ahora.
  document.addEventListener('tabChange', function () {
    refreshAll();
  });

  function init() {
    injectStyles();
    refreshAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // API pública
  window.loadCollectionByBranch = loadCollectionByBranch;
  window.BranchCollection = {
    load: loadCollectionByBranch,
    refreshAll: refreshAll,
    getLocationId: getLocationId
  };
})();
